import CardUserView from '@/components/Card/CardUser';
import CouponCom from '@/components/Coupon';
import { message } from '@/components/Message';
import Price from '@/components/Price';
import { calcCardSelectableInfo } from '@/models/card';
import { PreOrder } from '@/models/order';
import { PointRules } from '@/models/point';
import { calcSpecificationsTotalNumber } from '@/models/pos';
import { playSound } from '@/utils/sound';
import { CarryOutTwoTone, CloseOutlined, CreditCardTwoTone, FallOutlined, PushpinTwoTone } from '@ant-design/icons';
import { Col, Descriptions, Row, Typography } from 'antd';
import BigNumber from 'bignumber.js';
import React, { ComponentProps } from 'react';
import PointConsume from '../PointConsume';
import { useSettlementStore } from '../store/useSettlementStore';
import styles from '../style.less';
import WaiverAmountSelector from '../WaiverAmountSelector';

type Props = {
  preOrder: PreOrder;
  pointRules: PointRules;
  balancePayAvailable?: boolean;
};

/**
 * 购物车结算交易明细展示与抵扣交互区域
 *
 * 订阅 Zustand Store 中的各种衍生金额（商品原价、套餐卡抵扣、优惠券抵扣、积分抵用、抹零减免），
 * 并提供优惠券选择与套餐卡选择的触发逻辑。
 */
const CartTradeArea: React.FC<Props> = ({ preOrder, pointRules }: Props) => {
  // 从 Zustand Store 订阅购物车结算所需的各项派生金额与状态
  const writeOffCardUserList = useSettlementStore(s => s.writeOffCardUserList);
  const goodsPrice = useSettlementStore(s => s.goodsPrice);
  const cardPrice = useSettlementStore(s => s.cardPrice);
  const couponPrice = useSettlementStore(s => s.couponPrice);
  const pointPrice = useSettlementStore(s => s.pointPrice);
  const beforePointPrice = useSettlementStore(s => s.beforePointPrice);
  const beforeTotalPrice = useSettlementStore(s => s.beforeTotalPrice);
  const totalPrice = useSettlementStore(s => s.totalPrice);
  const pointNum = useSettlementStore(s => s.pointNum);
  const setPointNum = useSettlementStore(s => s.setPointNum);
  const waiverAmount = useSettlementStore(s => s.waiverAmount);
  const setWaiverAmount = useSettlementStore(s => s.setWaiverAmount);

  const selectedCoupon = useSettlementStore(s => s.selectedCoupon);
  const setSelectedCoupon = useSettlementStore(s => s.setSelectedCoupon);
  const setSelectedCards = useSettlementStore(s => s.setSelectedCards);

  // 优惠券结算可用基准价 = 商品原价 - 套餐卡抵扣金额
  const couponPriceTarget = (goodsPrice || new BigNumber(0)).minus(cardPrice || 0);

  // 如果提示自动勾选，则开启下面代码
  // useEffect(() => {
  //   // 挂载/换单后,子组件 CardUserView 的自动勾选已同步执行(子 effect 先于父 effect),
  //   // 此时 store 中的 writeOffCardUserList 即为最终核销结果,只有真正扣掉次数的卡才提示
  //   const list = useSettlementStore.getState().writeOffCardUserList || [];
  //   if (list.length === 0) return;

  //   // 按卡聚合核销数(同一张卡可能核销多个规格)
  //   const byCard = new Map<number, number>();
  //   list
  //     .filter(info => info.number > 0)
  //     .forEach(info => {
  //       byCard.set(info.cardUserId, (byCard.get(info.cardUserId) || 0) + info.number);
  //     });

  //   const cardList = preOrder.userFullInfo?.cardList || [];
  //   byCard.forEach((num, cardUserId) => {
  //     const card = cardList.find(c => c.id === cardUserId);
  //     if (card) {
  //       message.warning(`匹配套餐卡【${card.cardName}】已自动勾选，核销 ${num} 次，请注意核查`);
  //     }
  //   });
  // }, [preOrder]);

  return (
    <>
      {preOrder.userFullInfo && preOrder.userFullInfo.cardList.length > 0 && (
        <div className={styles.item}>
          <h3>
            <CreditCardTwoTone /> 客户持有套餐卡
          </h3>
          <div>
            <Row gutter={[18, 10]}>
              {preOrder.userFullInfo.cardList.map(card => {
                const currentWriteOffList = writeOffCardUserList || [];
                const selectedCardAndSkuInfoList = currentWriteOffList.filter(item => item.cardUserId === card.id);
                const selectedCount = selectedCardAndSkuInfoList.reduce((acc, cur) => acc + cur.number, 0);

                // 可核销上限计算与自动选择共用同一规则（见 useSettlementStore.init）
                const { matchesSku, available, max } = calcCardSelectableInfo(card, preOrder.specifications);

                const selectableInfo: ComponentProps<typeof CardUserView>['selectableInfo'] = {
                  max,
                  available,
                };

                if (matchesSku.length === 0) {
                  selectableInfo.NAReason = '无可用服务与商品';
                }

                return (
                  <Col span={24} key={card.id}>
                    <CardUserView
                      selectMode
                      card={card}
                      selectableInfo={selectableInfo}
                      selectCount={selectedCount}
                      onSelect={number => {
                        // 这里只登记 "期望值" 到 selectedCards,不产生任何扣减
                        // 扣减发生在: recalculatePrices → getCardPrice
                        const currentCards = useSettlementStore.getState().selectedCards || {};
                        setSelectedCards(Object.assign({}, currentCards, { [card.id]: number }));
                      }}
                    />
                  </Col>
                );
              })}
            </Row>
          </div>
        </div>
      )}

      {preOrder.userFullInfo && preOrder.userFullInfo.couponUserList.length > 0 && (
        <div className={styles.item}>
          <h3>
            <PushpinTwoTone /> 客户持有优惠券
          </h3>
          <div>
            <Row gutter={[18, 10]}>
              {preOrder.userFullInfo.couponUserList.map(coupon => {
                return (
                  <Col span={8} key={coupon.id}>
                    <CouponCom
                      price={couponPriceTarget}
                      coupon={coupon}
                      selected={selectedCoupon && selectedCoupon.id === coupon.id}
                      onInvalidSelected={reason => {
                        message.error(reason);
                      }}
                      onSelectedChange={(selected, selectedCpn) => {
                        playSound('click');
                        if (selected) {
                          setSelectedCoupon(selectedCpn);
                        } else {
                          setSelectedCoupon(null);
                        }
                      }}
                    />
                  </Col>
                );
              })}
            </Row>
          </div>
        </div>
      )}

      {preOrder && preOrder.userFullInfo && (
        <div className={styles.item}>
          <h3>
            <PushpinTwoTone /> 积分抵现
          </h3>
          <div>
            <PointConsume
              user={preOrder.userFullInfo.user}
              pointRules={pointRules}
              price={beforePointPrice}
              value={pointNum}
              onChange={val => setPointNum(val)}
            />
          </div>
        </div>
      )}

      <div className={styles.item}>
        <h3>
          <FallOutlined /> 特殊减免
        </h3>
        <div>
          <WaiverAmountSelector
            value={Number(waiverAmount || 0)}
            onChange={val => setWaiverAmount(val)}
            beforeTotalPrice={beforeTotalPrice}
          />
        </div>
      </div>

      <div className={styles.item}>
        <h3>
          <CarryOutTwoTone /> 交易清单如下，请收款
        </h3>
        <div>
          <Descriptions column={2} size={'small'} layout="horizontal" bordered>
            <Descriptions.Item label="商品总额">
              <Price value={goodsPrice} />
            </Descriptions.Item>
            <Descriptions.Item label="商品数量">
              {calcSpecificationsTotalNumber(preOrder.specifications)}
            </Descriptions.Item>

            {Boolean(cardPrice) && (
              <Descriptions.Item span={2} label="套餐卡抵扣">
                <Price value={cardPrice} negative />
              </Descriptions.Item>
            )}

            {Boolean(couponPrice) && (
              <Descriptions.Item span={2} label="优惠券抵扣">
                <Price value={couponPrice} negative />
              </Descriptions.Item>
            )}

            {writeOffCardUserList && (
              <>
                {writeOffCardUserList
                  .map(info => {
                    return (
                      <Descriptions.Item span={2} label="待核销" key={`${info.cardUserId}-${info.specificationId}`}>
                        {info.cardName}
                        <Typography.Text mark type="danger">
                          <CloseOutlined /> {info.number}
                        </Typography.Text>
                        {' - '}
                        {info.skuName}
                      </Descriptions.Item>
                    );
                  })
                  .filter(Boolean)}
              </>
            )}

            {Boolean(pointPrice) && (
              <Descriptions.Item span={2} label="积分抵扣">
                <Price value={pointPrice} negative />
              </Descriptions.Item>
            )}

            {Boolean(waiverAmount) && (
              <Descriptions.Item span={2} label="特殊减免">
                <Price value={waiverAmount} negative />
              </Descriptions.Item>
            )}

            <Descriptions.Item label="合计应收额" span={2}>
              <Price value={totalPrice} />
            </Descriptions.Item>
          </Descriptions>
        </div>
      </div>
    </>
  );
};

export default CartTradeArea;
